import { LOCALES, type Locale } from "@confluence/shared";
import { useId, useState, type FormEvent, type InputHTMLAttributes, type ReactNode } from "react";
import { ApiFailure, patch } from "./api";
import { LANGUAGE_NAMES, useI18n } from "./i18n";
import { useMe, useSetMe } from "./session";

/** Le chat Maki, sans fond ; version avec liseré clair pour le thème sombre. */
export function Logo({ size = 32 }: { size?: number }) {
  return (
    <span className="logo-mark" aria-hidden="true" style={{ width: size, height: size }}>
      <img src="/logo-cat.png" width={size} height={size} alt="" className="logo-light" />
      <img src="/logo-cat-dark.png" width={size} height={size} alt="" className="logo-dark" />
    </span>
  );
}

export function Brand() {
  return (
    <span className="brand">
      <Logo />
      <span className="brand-name">
        <b>maki</b> calendar
      </span>
    </span>
  );
}

/** Logo complet, en version claire ou sombre selon le thème. */
export function FullLogo() {
  return (
    <span className="full-logo" role="img" aria-label="Maki Calendar">
      <img src="/logo-full.png" alt="" className="logo-light" />
      <img src="/logo-full-dark.png" alt="" className="logo-dark" />
    </span>
  );
}

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string;
}

export function Field({ label, hint, error, ...input }: FieldProps) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input id={id} className="input" aria-invalid={!!error} aria-describedby={hint || error ? `${id}-d` : undefined} {...input} />
      {(error || hint) && (
        <small id={`${id}-d`} className={error ? "field-error" : "field-hint"}>
          {error ?? hint}
        </small>
      )}
    </div>
  );
}

export function Alert({ kind = "error", children }: { kind?: "error" | "ok" | "info"; children: ReactNode }) {
  return (
    <div className={`alert alert-${kind}`} role={kind === "error" ? "alert" : "status"}>
      {children}
    </div>
  );
}

/** Formulaire avec état d'envoi et erreurs d'API traduites, par champ quand l'API les précise. */
export function useForm<T>(submit: () => Promise<T>, onDone?: (r: T) => void) {
  const { errorText, t } = useI18n();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    setFields({});
    try {
      const r = await submit();
      onDone?.(r);
    } catch (err) {
      setError(errorText(err));
      if (err instanceof ApiFailure && err.fields) {
        setFields(Object.fromEntries(Object.keys(err.fields).map((k) => [k, t("error.field")])));
      }
    } finally {
      setPending(false);
    }
  };
  return { onSubmit, pending, error, fields, setError };
}

export function LanguageSelect({ compact }: { compact?: boolean }) {
  const { locale, setLocale, t } = useI18n();
  const me = useMe();
  const setMe = useSetMe();
  const id = useId();
  const change = async (l: Locale) => {
    setLocale(l);
    if (me.data) setMe((await patch<{ user: typeof me.data }>("/api/me", { locale: l })).user);
  };
  return (
    <div className={compact ? "lang lang-compact" : "field"}>
      <label htmlFor={id} className={compact ? "sr-only" : undefined}>
        {t("common.language")}
      </label>
      <select id={id} className="input" value={locale} onChange={(e) => change(e.target.value as Locale)}>
        {LOCALES.map((l) => (
          <option key={l} value={l}>
            {LANGUAGE_NAMES[l]}
          </option>
        ))}
      </select>
    </div>
  );
}

export function AuthCard({ title, children, footer }: { title: string; children: ReactNode; footer?: ReactNode }) {
  return (
    <main className="auth">
      <div className="auth-top">
        <LanguageSelect compact />
      </div>
      <FullLogo />
      <section className="auth-card">
        <h1>{title}</h1>
        {children}
      </section>
      {footer && <p className="auth-foot">{footer}</p>}
      <LegalLinks />
    </main>
  );
}

/** Liens vers la politique de confidentialité et les conditions d'utilisation. */
export function LegalLinks() {
  const { t } = useI18n();
  return (
    <p className="legal-links">
      <a href="/privacy">{t("legal.privacy")}</a>
      <span aria-hidden="true">·</span>
      <a href="/terms">{t("legal.terms")}</a>
    </p>
  );
}

export function timezones(): string[] {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return ["Europe/Paris", "Europe/Brussels", "Europe/Amsterdam", "Europe/Berlin", "Europe/Madrid", "Europe/Rome", "Europe/London", "UTC"];
  }
}
